import {Plural} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {DateCell} from '../../../../../../../../../src/components/common/table/cells/dateCell';
import {EmptyCell} from '../../../../../../../../../src/components/common/table/cells/emptyCell';
import {IconCell} from '../../../../../../../../../src/components/common/table/cells/iconCell';
import {NamespaceCell} from '../../../../../../../../../src/components/common/table/cells/namespaceCell';
import {NumberCell} from '../../../../../../../../../src/components/common/table/cells/numberCell';
import {
  handleVirtualTableCheckboxSelect,
  handleVirtualTableRowSelect,
} from '../../../../../../../../../src/components/common/table/helpers/tableHelpers';
import {TableColumn} from '../../../../../../../../../src/components/common/table/tableColumn';
import {
  TableRow,
  TableRowRenderOptions,
} from '../../../../../../../../../src/components/common/table/tableRow';
import {CardGroupModel} from '../../../../../../../../../src/models/api/cardGroupModel';
import {Handler, HandlerOf} from '../../../../../../../../shared/helpers/src/react/reactHelpers';
import {VisualSizesEnum} from '../../../../../../../../shared/ui/src/styles/commonStyles';
import {CardGroupsTableColumnsEnum} from './cardGroupsTableConfig';

/*
 * Props.
 */

interface CardGroupsTableRowProps {
  isSelected: boolean;
  isPreviousSiblingSelected: boolean;
  isNextSiblingSelected: boolean;
  isInMultiSelectMode?: boolean;
  cardGroup?: CardGroupModel;
  tableColumns: ReadonlyArray<TableColumn<CardGroupsTableColumnsEnum>>;
  options: TableRowRenderOptions;
  onMultiSelect: Handler;
  onCheckBoxToggle: HandlerOf<boolean>;
  onSelect: Handler;
}

/*
 * Style.
 */

interface StyledTableRowProps {
  $minHeight: number;
}
const StyledTableRow = styled(TableRow)<StyledTableRowProps>`
  min-height: ${(p) => p.$minHeight}px;
`;

/*
 * Component.
 */

export const CardGroupsTableRow: FC<CardGroupsTableRowProps> = (props) => {
  const {
    cardGroup,
    options,
    tableColumns,
    isSelected,
    isNextSiblingSelected,
    isPreviousSiblingSelected,
    isInMultiSelectMode,
    onSelect,
    onMultiSelect,
    onCheckBoxToggle,
  } = props;
  if (!cardGroup) {
    return null;
  }

  const cellRenderer = (
    index: number,
    column: TableColumn<CardGroupsTableColumnsEnum>,
    isHovering: boolean,
    shouldShowStickyGradient: boolean,
  ) => {
    switch (column.type) {
      case CardGroupsTableColumnsEnum.NAME:
        return (
          <IconCell
            key={CardGroupsTableColumnsEnum.NAME}
            value={cardGroup.name}
            settings={column.settings}
            isHovering={isHovering}
            iconSize={VisualSizesEnum.MEDIUM}
            iconName="listFilledSmall"
            size={options.size}
            index={index}
            isSelected={isSelected}
            shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
            shouldRemoveBottomBorderRadius={isNextSiblingSelected}
            shouldShowStickyGradient={shouldShowStickyGradient}
          />
        );
      case CardGroupsTableColumnsEnum.ACCESS:
        return (
          <NamespaceCell
            key={CardGroupsTableColumnsEnum.ACCESS}
            value={cardGroup.namespace}
            settings={column.settings}
            isHovering={isHovering}
            size={options.size}
            index={index}
            isSelected={isSelected}
            shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
            shouldRemoveBottomBorderRadius={isNextSiblingSelected}
            shouldShowStickyGradient={shouldShowStickyGradient}
          />
        );
      case CardGroupsTableColumnsEnum.CONTACTS:
        return (
          <NumberCell
            key={CardGroupsTableColumnsEnum.CONTACTS}
            value={cardGroup.numberOfCards}
            settings={column.settings}
            isHovering={isHovering}
            size={options.size}
            index={index}
            isSelected={isSelected}
            shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
            shouldRemoveBottomBorderRadius={isNextSiblingSelected}
            shouldShowStickyGradient={shouldShowStickyGradient}
            renderValue={(numberOfCards) => (
              <Plural
                id="card-groups-table-col-total-contacts-text"
                value={numberOfCards}
                one="1 contact"
                other="# contacts"
              />
            )}
          />
        );
      case CardGroupsTableColumnsEnum.LAST_USED:
        return (
          <DateCell
            key={CardGroupsTableColumnsEnum.LAST_USED}
            value={`${cardGroup.updatedAt}`}
            settings={column.settings}
            isHovering={isHovering}
            size={options.size}
            index={index}
            isSelected={isSelected}
            shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
            shouldRemoveBottomBorderRadius={isNextSiblingSelected}
            shouldShowStickyGradient={shouldShowStickyGradient}
            customFormat={{
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            }}
          />
        );
      case CardGroupsTableColumnsEnum.DATE_CREATED:
        return (
          <DateCell
            key={CardGroupsTableColumnsEnum.DATE_CREATED}
            value={`${cardGroup.createdAt}`}
            settings={column.settings}
            isHovering={isHovering}
            size={options.size}
            index={index}
            isSelected={isSelected}
            shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
            shouldRemoveBottomBorderRadius={isNextSiblingSelected}
            shouldShowStickyGradient={shouldShowStickyGradient}
            customFormat={{
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            }}
          />
        );
      default: {
        return (
          <EmptyCell
            key={column.type}
            settings={column.settings}
            isHovering={isHovering}
            size={options.size}
            index={index}
            isSelected={isSelected}
            shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
            shouldRemoveBottomBorderRadius={isNextSiblingSelected}
          />
        );
      }
    }
  };

  return (
    <StyledTableRow
      renderColumn={(isHovering) =>
        tableColumns.map((column, index) =>
          cellRenderer(index, column, isHovering, Boolean(options.shouldShowStickyGradient)),
        )
      }
      onSelect={(event) =>
        handleVirtualTableRowSelect(event, {
          isSelected,
          isInMultiSelectMode,
          onSelect,
          onCheckBoxToggle,
          onMultiSelect,
        })
      }
      onCheckBoxClick={(event) =>
        handleVirtualTableCheckboxSelect(event, {isSelected, onCheckBoxToggle, onMultiSelect})
      }
      hasHover
      isSelected={isSelected}
      $minHeight={options.tableRowHeight}
      shouldShowRightGradient={options.shouldShowRightGradient}
      shouldAllowMultiSelect={options.shouldAllowMultiSelect}
      isCheckboxesVisible={isInMultiSelectMode}
    />
  );
};
